import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle, KeyRound } from 'lucide-react';
import { C } from '../theme';
import planiqLogo from '../assets/logo.png';

const API = process.env.REACT_APP_API_URL || '';

const inputStyle = {
  width: '100%', padding: '10px 12px',
  background: 'rgba(255,255,255,0.8)', border: '1px solid #c8d8e8',
  borderRadius: 8, color: '#1a2a3a', fontSize: 14,
  outline: 'none', boxSizing: 'border-box', transition: 'border .15s',
};
const labelStyle = {
  display: 'block', color: '#6a8aaa', fontSize: 11,
  fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6,
};

function FocusInput({ type = 'text', ...props }) {
  return (
    <input type={type} {...props} style={inputStyle}
      onFocus={e => e.target.style.borderColor = '#0090d0'}
      onBlur={e => e.target.style.borderColor = '#c8d8e8'}
    />
  );
}

const bg = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 20,
  background: 'linear-gradient(135deg, #e8eef5 0%, #dce6f0 40%, #e4ecf5 70%, #d8e8f2 100%)',
  position: 'relative', overflow: 'hidden',
};

export default function InviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [alreadyAccepted, setAlreadyAccepted] = useState(null);
  const [form, setForm] = useState({ full_name: '', password: '', password_confirm: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/invitations/validate?token=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(d => {
        if (d.detail && typeof d.detail === 'object' && d.detail.already_accepted) {
          setAlreadyAccepted(d.detail.email || '');
        } else if (d.detail) {
          setLoadError(typeof d.detail === 'string' ? d.detail : 'Invalid or expired invitation.');
        } else {
          setInvite(d);
        }
      })
      .catch(() => setLoadError('Could not load invitation.'));
  }, [token]);

  function set(field) {
    return e => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.full_name.trim()) { setError('Please enter your full name.'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (form.password !== form.password_confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/invitations/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, full_name: form.full_name, password: form.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to accept invitation');
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={bg}>
      {/* Decorative shapes */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', bottom: '8%', left: '8%', width: 120, height: 120, background: 'linear-gradient(135deg, #fff 60%, #c8d8e8)', borderRadius: 18, boxShadow: '8px 8px 32px rgba(100,140,180,0.18)', transform: 'rotate(-12deg)' }}/>
        <div style={{ position: 'absolute', bottom: '38%', left: '14%', width: 44, height: 44, background: 'linear-gradient(135deg, #e8f0f8, #d0e0ee)', borderRadius: 8, transform: 'rotate(-8deg)' }}/>
        <div style={{ position: 'absolute', bottom: '55%', left: '22%', width: 32, height: 32, background: 'linear-gradient(135deg, #f0f6fb, #dceaf5)', borderRadius: 6, transform: 'rotate(5deg)' }}/>
        {[[85,20],[92,35],[78,45],[88,58],[95,70],[80,72],[90,82]].map(([l,t],i)=>(
          <div key={i} style={{ position: 'absolute', left: l+'%', top: t+'%', width: 6, height: 6, borderRadius: '50%', background: 'rgba(150,180,210,0.5)' }}/>
        ))}
      </div>

      <div style={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div>
            <img src={planiqLogo} alt="PlanIQ" style={{ width: 240, height: 'auto' }} />
          </div>
          <div style={{ fontSize: 13, color: '#8aa0b8', fontFamily: 'IBM Plex Mono, monospace' }}>planiq.live</div>
        </div>

        <div style={{
          background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.9)', borderRadius: 16,
          padding: '36px 36px 28px', boxShadow: '0 8px 40px rgba(80,120,160,0.12)',
        }}>
          {alreadyAccepted ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ marginBottom: 14, display:'flex', justifyContent:'center' }}><KeyRound size={44} color='#0090d0' strokeWidth={1.5}/></div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1a2a3a', marginBottom: 10 }}>Account already set up</div>
              <div style={{ fontSize: 14, color: '#6a8aaa', marginBottom: 20, lineHeight: 1.5 }}>
                An account for <strong>{alreadyAccepted}</strong> already exists. If you're having trouble logging in, reset your password below.
              </div>
              <button onClick={() => navigate('/login', { state: { openForgot: true, forgotEmail: alreadyAccepted } })} style={{ padding: '10px 24px', background: 'linear-gradient(135deg,#0090d0,#0060a0)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                Reset Password
              </button>
            </div>
          ) : loadError ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ marginBottom: 14, display:'flex', justifyContent:'center' }}><AlertTriangle size={44} color='#d04040' strokeWidth={1.5}/></div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#d04040', marginBottom: 10 }}>Invalid invitation</div>
              <div style={{ fontSize: 14, color: '#6a8aaa', marginBottom: 20 }}>{loadError}</div>
              <button onClick={() => navigate('/login')} style={{ padding: '10px 24px', background: 'linear-gradient(135deg,#0090d0,#0060a0)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                Back to Sign In
              </button>
            </div>
          ) : !invite ? (
            <div style={{ textAlign: 'center', color: '#6a8aaa', padding: '20px 0' }}>Validating invitation…</div>
          ) : done ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ marginBottom: 14, display:'flex', justifyContent:'center' }}><CheckCircle size={44} color='#2a8a5a' strokeWidth={1.5}/></div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1a2a3a', marginBottom: 10 }}>Account created!</div>
              <div style={{ fontSize: 14, color: '#4a6a8a', marginBottom: 22 }}>
                Welcome to <strong>{invite.org_name}</strong>. You can now sign in with your email and password.
              </div>
              <button onClick={() => navigate('/login')} style={{ padding: '10px 24px', background: 'linear-gradient(135deg,#0090d0,#0060a0)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 16px rgba(0,120,200,0.25)' }}>
                Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1a2a3a', marginBottom: 6 }}>You've been invited!</div>
                <div style={{ fontSize: 13, color: '#6a8aaa', lineHeight: 1.5 }}>
                  <strong>{invite.inviter_name}</strong> invited you to join{' '}
                  <strong>{invite.org_name}</strong> as{' '}
                  <strong>{invite.role.replace(/_/g, ' ')}</strong>.
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Email</label>
                <input value={invite.email} disabled style={{ ...inputStyle, background: 'rgba(240,246,252,0.8)', color: '#6a8aaa' }} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Your full name</label>
                <FocusInput value={form.full_name} onChange={set('full_name')} placeholder="Jane Smith" required autoFocus />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Password</label>
                <FocusInput type="password" value={form.password} onChange={set('password')} placeholder="Min. 8 characters" required />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Confirm password</label>
                <FocusInput type="password" value={form.password_confirm} onChange={set('password_confirm')} placeholder="Repeat password" required />
              </div>

              {error && (
                <div style={C.feedback('danger')}>{error}</div>
              )}

              <button type="submit" disabled={loading} style={{
                width: '100%', padding: '12px',
                background: loading ? '#c0d0e0' : 'linear-gradient(135deg, #0090d0, #0060a0)',
                border: 'none', borderRadius: 8, color: '#fff',
                fontWeight: 700, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 16px rgba(0,120,200,0.25)', transition: 'all .15s',
              }}>
                {loading ? 'Creating account…' : 'Accept & Create Account'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
